<template>
  <div>
    <app-cells>
      <h1 class="title">UTT Balance</h1>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="period"
          :custom-shortcuts="custom_shortcuts"
          shortcut="thisYear"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
    </app-cells>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <div class="utt-table">
        <div v-for="(item, index) in utt_table" :key="index" class="utt-table__item">
          <div class="utt-table__employee">
            <div class="utt-table__avatar">
              <img :src="getUserAvatar(item.crop.crop_url)" alt="">
            </div>
            <div class="utt-table__name">{{ item.full_name }}</div>
          </div>
          <div class="utt-table__period">
            <button
              @click="onShowMakePaymentPopup(item, period.month)"
              v-for="(period, period_index) in item.months"
              :key="period_index"
              type="button"
              class="utt-table__col"
            >
              <div class="utt-table__date">{{ dateFormat(period.month) }}</div>
              <div
                class="utt-table__balance"
                :class="{
                  'utt-table__balance--red': period.balance > 0,
                  'utt-table__balance--blue': period.balance < 0,
                  'utt-table__balance--green': period.balance === 0
                }"
              >
                <template v-if="period.balance !== 0">
                  {{ period.balance.toLocaleString() }} {{ period.currency.code }}
                </template>
                <template v-else>paid</template>
              </div>
            </button>
          </div>
        </div>
      </div>
      <modal
        name="make-payment"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('make-payment')" class="popup__close" type="button"></button>
          <div class="popup__title">Make payment</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckForm">
              <app-form-group label="Employee" label-for="employee" required>
                <app-input
                  v-model="form.employee_name"
                  id="employee"
                  placeholder="Employee"
                  name="employee"
                  readonly=""
                />
              </app-form-group>
              <app-form-group label="Payment date" required>
                <app-date-picker
                  v-model="form.payment_date"
                  placeholder="Check-in"
                  :error="$v.form.payment_date.$error"
                />
                <template #error>
                  <div v-if="$v.form.payment_date.$dirty && !$v.form.payment_date.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Account" required>
                <app-select
                  v-model="form.account"
                  :options="account_options"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Account"
                  :error="$v.form.account.$error"
                />
                <template #error>
                  <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Amount" label-for="amount" required>
                <app-fin-number
                  v-model="form.amount"
                  id="amount"
                  placeholder="0"
                  name="amount"
                />
              </app-form-group>
              <app-form-group label="Related month" required>
                <app-date-picker
                  v-model="form.related_month"
                  placeholder="Payment date"
                  readonly
                  :error="$v.form.related_month.$error"
                />
                <template #error>
                  <div v-if="$v.form.related_month.$dirty && !$v.form.related_month.required">Required field</div>
                </template>
              </app-form-group>
              <app-cells position="center">
                <app-button type="submit" ref="submit" :disabled="$v.form.$error">Pay</app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
import { getEmployeesUTTBalance, getFinanceAccounts, postFinancePaymentsUTTSalaries } from '@/http'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { format, parse } from 'date-fns'
import moment from 'moment'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'EmployeesUTTBalance',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      is_loaded: false,
      utt_table: {},
      date_picker_error: false,
      period: null,
      custom_shortcuts: [
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      form: {},
      account_options: [],
    }
  },
  mounted() {
    this.fetchItems()
    getFinanceAccounts().then(response => { this.account_options = response.data })
  },
  validations: {
    form: {
      employee: { required },
      payment_date: { required },
      account: { required },
      amount: { required },
      related_month: { required },
    },
  },
  methods: {
    fetchItems() {
      getEmployeesUTTBalance(this.period.start, this.period.end)
        .then(res => {
          this.utt_table = res.data.employees.map(item => {
            return {
              ...item,
              months: item.months.map(period => {
                return {
                  ...period,
                  balance: parseFloat(period.balance)
                }
              })
            }
          })
          this.is_loaded = true
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postFinancePaymentsUTTSalaries(this.form)
        .finally(() => { this.$refs.submit.preload = false })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Payment made successfully',
          })
          this.$modal.hide('make-payment')
        })
    },
    onShowMakePaymentPopup(item, month) {
      this.$modal.show('make-payment')
      this.form = {
        employee: item.id,
        employee_name: item.full_name,
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        related_month: month
      }
    },
    onIsHiddenPicker() {
      if (this.period?.start && this.period?.end) {
        this.date_picker_error = false
        this.fetchItems()
      } else this.date_picker_error = true
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yy') : '-'
    },
  }
}
</script>
<style lang="sass" scoped>
.utt-table
  display: flex
  flex-direction: column
  align-items: flex-start

.utt-table__item
  display: flex
  margin-bottom: 10px
  border: 1px solid #eaeaea
  border-radius: 7px
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04)
  background-color: #fff
  overflow: hidden

  &:last-child
    margin-bottom: 0

.utt-table__employee
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 0 12px
  width: 190px
  border-right: 1px solid #eaeaea
  padding: 12px

.utt-table__avatar
  flex-shrink: 0
  width: 36px
  height: 36px
  line-height: 0
  border-radius: 50%
  overflow: hidden

  img
    width: 100%
    height: 100%
    object-fit: cover

.utt-table__name
  font-weight: 500

.utt-table__period
  display: flex

.utt-table__col
  display: flex
  flex-direction: column
  width: 115px
  border-right: 1px solid #eaeaea
  cursor: pointer

  &:hover
    .utt-table__balance
      background-color: #f6f6f6

  &:last-child
    border-right: none

.utt-table__date
  flex-shrink: 0
  width: 100%
  padding: 2px 5px 1px
  border-bottom: 1px solid #eaeaea
  background-color: #f6f6f6
  color: #5f5f5f
  text-align: center

.utt-table__balance
  flex-grow: 1
  display: flex
  align-items: center
  justify-content: center
  width: 100%
  background-color: #fff
  transition: background-color 0.3s

.utt-table__balance--red
  color: #e85c20

.utt-table__balance--blue
  color: #4188b5

.utt-table__balance--green
  color: #77b017

</style>
